/**
 * @file AccountLanguageSwitcher.tsx
 *
 * The language control inside the account drawer's Preferences group: a row
 * showing the current language (its autonym) that expands to the list of
 * supported admin-UI locales. Selecting one switches the locale via
 * {@link useLocale} and collapses the list. It owns only its own expand state,
 * so switching language never closes the surrounding drawer (the drawer reloads
 * the new catalog in place). Labels are autonyms from {@link languageLabel}.
 */

import { useState } from 'react';
import { Icon } from '@group.one/react-gravity';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@wsb/i18n';
import { useLocale } from '../../i18n/locale-context';
import { languageLabel } from '../i18n/language-labels';
import styles from './AccountMenu.module.css';

/**
 * Renders the expandable language switcher row.
 *
 * @returns The current-language row and, when expanded, the locale option list.
 */
export function AccountLanguageSwitcher() {
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);

  const select = (next: SupportedLocale): void => {
    setLocale(next);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.item} gv-flex gv-items-center gv-justify-between gv-w-full gv-p-sm`}
        data-testid="account-menu-language"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="gv-inline-flex gv-items-center gv-gap-sm gv-min-w-0">
          <Icon name="language" size="small" />
          <span className="gv-text-sm">{languageLabel(locale)}</span>
        </span>
        <Icon
          name={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          size="small"
        />
      </button>

      {open && (
        <div className="gv-flex gv-flex-col gv-gap-xs gv-pl-lg">
          {SUPPORTED_LOCALES.map((code) => {
            const active = code === locale;
            return (
              <button
                key={code}
                type="button"
                data-testid="account-menu-language-option"
                data-locale={code}
                aria-current={active ? 'true' : undefined}
                className={`${styles.submenuItem} gv-flex gv-items-center gv-w-full gv-p-sm${
                  active ? ` ${styles.submenuItemActive}` : ''
                }`}
                onClick={() => select(code)}
              >
                <span className="gv-text-sm">{languageLabel(code)}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
